import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
} from 'lucide-react';

interface Inspection {
  id: string;
  vehicle_name: string;
  type: string;
  date: string;
  status: 'pending' | 'passed' | 'failed' | 'requires_attention';
  inspector: string;
  expiry: string;
}

interface InspectionsResponse {
  inspections: Inspection[];
  metrics: {
    total: number;
    passed: number;
    failed: number;
    expiring_soon: number;
  };
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  passed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  requires_attention: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  passed: 'Passed',
  failed: 'Failed',
  requires_attention: 'Attention',
};

export default function InspectionsPage() {
  const { data, isLoading } = useApi<InspectionsResponse>('/api/inspections');
  const [showNew, setShowNew] = useState(false);

  const metrics = data?.metrics ?? { total: 0, passed: 0, failed: 0, expiring_soon: 0 };
  const inspections = data?.inspections ?? [];

  const metricCards = [
    { label: 'Total Inspections', value: metrics.total, icon: ClipboardCheck, color: 'text-emerald-400' },
    { label: 'Passed', value: metrics.passed, icon: CheckCircle2, color: 'text-emerald-400' },
    { label: 'Failed', value: metrics.failed, icon: XCircle, color: 'text-red-400' },
    { label: 'Expiring Soon', value: metrics.expiring_soon, icon: AlertTriangle, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-zinc-100">Inspections</h1>
        </div>
        <button
          onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Inspection
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl border border-zinc-800 bg-zinc-900 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-zinc-400">{m.label}</span>
              <m.icon className={`h-5 w-5 ${m.color}`} />
            </div>
            <p className="mt-2 text-2xl font-semibold text-zinc-100">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-400">
                <th className="px-4 py-3 font-medium">Vehicle</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Inspector</th>
                <th className="px-4 py-3 font-medium">Expiry</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">Loading inspections...</td>
                </tr>
              ) : inspections.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">No inspections found</td>
                </tr>
              ) : (
                inspections.map((item) => (
                  <tr key={item.id} className="hover:bg-zinc-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-zinc-100">{item.vehicle_name}</td>
                    <td className="px-4 py-3 text-zinc-300">{item.type}</td>
                    <td className="px-4 py-3 text-zinc-400">{new Date(item.date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColors[item.status] ?? ''}`}>
                        {statusLabels[item.status] ?? item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{item.inspector}</td>
                    <td className="px-4 py-3 text-zinc-400">{new Date(item.expiry).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
